import { TableColumn } from '@/components';
import { IViewCard } from '@/components/Cards';
import { DataGridColSettings } from '@/components/DataGrid/utils';
import { IMasterVehicleForm } from '@/models';
import { FileAcceptedTypes } from '@/pages/Kases/KaseFiles/components/data';

export enum MasterVehicleScreens {
  MasterVehicleList = 'MasterVehicleList',
  MasterVehicleForm = 'MasterVehicleForm',
  MasterVehicleLookup = 'MasterVehicleLookup',
  MasterVehicleOwnerList = 'MasterVehicleOwnerList',
  MasterVehicleView = 'MasterVehicleView',
  TabNameNotes = 'TabNameNotes',
  MasterVehicleNotes = 'MasterVehicleNotes',
}

export const MasterVehicleNextPaths = {
  MasterVehicleList: [
    MasterVehicleScreens.MasterVehicleView,
    MasterVehicleScreens.MasterVehicleForm,
  ],
  MasterAddVehicleDetail: [
    MasterVehicleScreens.MasterVehicleLookup,
    MasterVehicleScreens.MasterVehicleOwnerList,
  ],
};

export const masterVehicleDataGridCols: DataGridColSettings[] = [
  {
    field: 'licensePlate',
    headerName: 'License Plate Number',
    width: 180,
    visible: true,
  },
  {
    field: 'licensePlateState',
    headerName: 'License Plate State',
    width: 180,
    visible: true,
  },
  {
    field: 'color',
    headerName: 'Color',
    width: 320,
    visible: true,
  },
  {
    field: 'make',
    headerName: 'Make',
    width: 320,
    visible: true,
  },
  {
    field: 'model',
    headerName: 'Model',
    width: 320,
    visible: true,
  },
  {
    field: 'disposition',
    headerName: 'Disposition',
    width: 180,
    visible: true,
  },
  {
    field: 'isVerified',
    headerName: 'Data Source',
    width: 320,
    visible: true,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    width: 150,
    visible: true,
  },
];

export const MasterVehiclesearchTableColumns: TableColumn[] = [
  { key: 'licensePlate.number', label: 'License Plate Number' },
  { key: 'licensePlate.state', label: 'Plate state' },
  { key: 'color', label: 'Color' },
  { key: 'make.name', label: 'Make' },
  { key: 'model.name', label: 'Model' },
  { key: 'vin', label: 'VIN' },
  { key: 'year', label: 'Year' },
  { key: 'useInfo', label: '' },
];

export const MasterVehicleOwnerTableViewColumns: TableColumn[] = [
  { key: 'name', label: 'Name' },
  { key: 'phoneNumber', label: 'Phone number' },
  { key: 'addresses', label: 'Address' },
  { key: 'type', label: 'Type' },
];

export const MasterVehicleOwnerTableColumns: TableColumn[] = [
  ...MasterVehicleOwnerTableViewColumns,
  { key: 'action', label: '' },
];

const validateIfVerified = (
  currentValue: string,
  formValues: IMasterVehicleForm,
) => {
  return formValues.isVerified && !currentValue
    ? 'This field is required.'
    : true;
};
const startYear = new Date().getFullYear();
export const vehicleYears = Array.from({ length: 125 }, (_, index) => {
  const year = (startYear - index).toString();
  return { value: year, label: year };
});

export const MasterVehicleFormFields = [
  {
    key: 'information',
    title: 'Vehicle information',
    items: [
      {
        name: 'vin',
        label: 'VIN number',
        rules: {
          maxLength: {
            value: 20,
            message: 'VIN should be less than 20 characters long.',
          },
        },
      },
    ],
  },
  {
    key: 'description',
    title: 'Vehicle description',
    items: [
      {
        name: 'type',
        label: 'Vehicle type',
        type: 'autocomplete',
        rules: {
          validate: validateIfVerified,
        },
      },
      {
        name: 'uniqueCharacteristics',
        label: 'Unique characteristics',
        rules: {
          maxLength: {
            value: 100,
            message: 'Characteristics should be less than 100 characters',
          },
        },
        grid: {
          md: 9,
          lg: 9,
        },
      },
    ],
  },
  {
    key: 'photos',
    title: 'Photos',
    items: [
      {
        name: 'files',
        type: 'file',
        accept: FileAcceptedTypes,
        label: '',
        grid: {
          md: 12,
          lg: 12,
        },
      },
    ],
  },
  {
    key: 'ownerInformation',
    title: 'Registered owner information',
    items: [],
  },
  {
    key: 'verified',
    title: '',
    items: [
      {
        name: 'isVerified',
        type: 'checkbox',
        label: 'Verified',
        sx: {
          fontWeight: 600,
        },
        grid: {
          md: 6,
        },
      },
    ],
  },
];

export const AutomobileTypeFormFields = [
  {
    name: 'year',
    label: 'Year',
    type: 'autocomplete',
    items: vehicleYears,
  },
  {
    name: 'make',
    label: 'Make',
    type: 'autocomplete',
    multiple: false,
    rules: {
      validate: validateIfVerified,
    },
  },
  {
    name: 'model',
    label: 'Model',
    type: 'autocomplete',
    rules: {
      validate: validateIfVerified,
    },
    multiple: false,
  },
  {
    name: 'color',
    label: 'Color',
    type: 'autocomplete',
    clearOnBlur: false,
    freeSolo: true,
    // rules: {
    //   validate: validateIfVerified,
    // },
  },
  {
    name: 'style',
    label: 'Style',
    type: 'text',
  },
];

export const VehicleTypeFormFields = [
  {
    name: 'year',
    label: 'Year',
    type: 'autocomplete',
    items: vehicleYears,
  },
  {
    name: 'make',
    label: 'Make',
    rules: {
      validate: validateIfVerified,
    },
  },
  {
    name: 'model',
    label: 'Model',
    rules: {
      validate: validateIfVerified,
    },
  },
  {
    name: 'color',
    label: 'Color',
  },
];

export const MasterVehicleViewFields: IViewCard[] = [
  {
    title: 'Vehicle information',
    key: 'vehicleInformation',
    size: { md: 2 },
    items: [
      {
        label: 'VIN number',
        value: 'vin',
      },
    ],
  },
  {
    title: 'Vehicle description',
    key: 'vehicleDescription',
    size: { md: 2 },
    items: [
      {
        label: 'Type',
        value: 'type',
      },
      {
        label: 'Make',
        value: 'make',
      },
      {
        label: 'Model',
        value: 'model',
      },
      {
        label: 'Year',
        value: 'year',
      },
      {
        label: 'Color',
        value: 'color',
      },
      {
        label: 'Style',
        value: 'style',
      },
      {
        label: 'Unique characteristics',
        value: 'uniqueCharacteristics',
      },
    ],
  },
  {
    title: 'Registered owner information',
    key: 'ownerinfo',
    size: { md: 2 },
    items: [],
  },
  {
    title: 'Verification status',
    key: 'verificationStatus',
    items: [
      {
        label: 'Verification Status',
        value: 'isVerified',
      },
    ],
  },
  {
    title: 'Photos',
    key: 'photos',
    items: [
      {
        label: 'Photos',
        value: 'files',
      },
    ],
  },
];

export const MasterVehicleOwnersTempdata = [
  {
    lastName: 'Last name',
    firstName: 'First name',
    homePhoneNumber: 'Home phone number',
    cellPhoneNumber: 'Cell phone number',
    address: 'Address',
  },
];

export const MasterVehicleOwnerSearchFields = [
  {
    name: 'ownerType',
    label: 'Owner type',
    type: 'select',
    items: [
      { value: 'PERSON', label: 'Person' },
      { value: 'ORGANIZATION', label: 'Organization' },
    ],
  },
  {
    name: 'lastName',
    label: 'Last name',
    type: 'text',
    showWhen: (values: { ownerType: string }) => values.ownerType === 'PERSON',
  },
  {
    name: 'firstName',
    label: 'First name',
    type: 'text',
    showWhen: (values: { ownerType: string }) => values.ownerType === 'PERSON',
  },
  {
    name: 'organizationName',
    label: 'Organization name',
    type: 'text',
    showWhen: (values: { ownerType: string }) =>
      values.ownerType === 'ORGANIZATION',
  },
  {
    name: 'address',
    label: 'Address',
    type: 'address',
  },
];

export const MasterVehicleSearchFields = [
  {
    name: 'plateNumber',
    label: 'License Plate Number',
    rules: {
      minLength: {
        value: 4,
        message:
          'License Plate Number search text should be more than 4 characters.',
      },
      maxLength: {
        value: 20,
        message: 'License Plate Number is less than 20 characters.',
      },
    },
  },
  {
    name: 'vin',
    label: 'VIN',
    rules: {
      maxLength: {
        value: 20,
        message: 'VIN is less than 20 characters.',
      },
    },
  },
];
